// src/lib/tbilisiTime.js — meeting wall-clock time is TBILISI time, never the device's.
//
// OWNER RULING 2026-08-12: "meeting calculates from devices time and thats wrong we shoudl
// use actual georgia tbilisi time for calculaton."
//
// The bug this kills had three faces, all the same root — `new Date("YYYY-MM-DDTHH:mm")`
// and `toLocaleString()` both silently use the DEVICE timezone:
//   • WRITE: an agent typing 15:00 means 15:00 in Tbilisi; a device set to any other zone
//     stored a different instant.
//   • EDIT SEED: decomposing scheduled_at with getHours()/getMinutes() showed the device's
//     wall clock back in the form, so re-saving an untouched form MOVED the meeting.
//   • DISPLAY: the feed and modal printed device-zone times.
//
// Georgia abolished DST in 2005 — Asia/Tbilisi is permanent UTC+4, which is why compose can
// use a fixed offset. Display still goes through Intl with the named zone, so if that fact
// ever changes, only OFFSET below needs touching.
//
// Countdown math (MeetingCountdown) needs none of this: it compares absolute epochs and is
// timezone-independent by construction — do not "fix" it.

import { getLanguage } from './i18n.js'

export const TBILISI_TZ = 'Asia/Tbilisi'
const OFFSET = '+04:00' // permanent; no DST since 2005

// "2026-08-12" + "15:00" (agent's intent: Tbilisi wall clock) → UTC ISO instant.
// Falsy time anchors at noon, matching the previous call sites' `|| '12:00'` defaults.
export function composeTbilisi(date, time) {
  if (!date) return null
  const d = new Date(`${date}T${time || '12:00'}:00${OFFSET}`)
  return isNaN(d) ? null : d.toISOString()
}

// The reverse: an ISO instant → {date:'YYYY-MM-DD', time:'HH:mm'} in Tbilisi wall clock,
// for seeding <input type=date> / <input type=time>. Round-trips with composeTbilisi.
export function tbilisiParts(iso) {
  if (!iso) return { date: '', time: '' }
  const d = new Date(iso)
  if (isNaN(d)) return { date: '', time: '' }
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: TBILISI_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d).map(x => [x.type, x.value]))
  // Intl can emit hour "24" for midnight in some engines; normalise.
  const hh = p.hour === '24' ? '00' : p.hour
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${hh}:${p.minute}` }
}

// Drop-in for `new Date(iso).toLocaleString(undefined, opts)` — same options, Tbilisi zone.
// ⚠️ In the APP's language, not the browser's (Desk home audit 2026-10-21): a Georgian Desk on an English browser
// printed "Thu", "Oct", "PM". The zone stays Tbilisi either way.
export function fmtTbilisi(iso, opts = {}) {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d) ? '' : d.toLocaleString(getLanguage() === 'ka' ? 'ka-GE' : 'en-GB', { ...opts, timeZone: TBILISI_TZ })
}

// "Which instants belong to Georgian day YYYY-MM-DD" — for the day windows the activity
// rail queries with. Device-zone windows shifted day membership by the device offset.
export function tbilisiDayWindow(dateStr) {
  return {
    startIso: new Date(`${dateStr}T00:00:00${OFFSET}`).toISOString(),
    endIso: new Date(`${dateStr}T23:59:59.999${OFFSET}`).toISOString(),
  }
}

// Minute-of-Tbilisi-day for an instant — the rail's vertical position.
export function tbilisiMinuteOfDay(iso) {
  const { time } = tbilisiParts(iso)
  if (!time) return 0
  const [h, m] = time.split(':')
  return Number(h) * 60 + Number(m)
}

// Today's date string in Tbilisi ("what day is it in Georgia", not on the device).
export function tbilisiToday() {
  return tbilisiParts(new Date().toISOString()).date
}
